/**
 * iter-380 — Highlight matched text inside description and annexure cells
 * so admins can visually locate what the Find/Replace bar is targeting.
 *
 * Usage:
 *   <HighlightedText text={raw} query={findQuery} caseSensitive={false} html />
 *
 * Wraps every occurrence of `query` in a `<mark>` element with a soft
 * amber background. Escapes the surrounding text so we never leak
 * raw HTML from an untrusted source.
 */

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Returns an object suitable for `dangerouslySetInnerHTML` that renders
 * `text` with every match of `query` wrapped in a highlighted `<mark>`.
 *
 * When `preHtml` is true, `text` is treated as already-rendered light
 * markdown HTML (from mdRender) and only the query is highlighted on
 * top; other tags in the text are preserved.
 */
export function highlightHtml(text, query, { caseSensitive = false, preHtml = false } = {}) {
  const raw = String(text ?? '');
  const q = String(query ?? '').trim();
  if (!q) return { __html: preHtml ? raw : escapeHtml(raw).replace(/\n/g, '<br/>') };
  const source = preHtml ? raw : escapeHtml(raw).replace(/\n/g, '<br/>');
  const re = new RegExp(escapeRegex(q), caseSensitive ? 'g' : 'gi');
  return { __html: source.replace(re, (m) => `<mark class="bg-amber-200 text-amber-900 rounded px-0.5 py-[1px] font-medium">${m}</mark>`) };
}

export default function HighlightedText({ text, query, caseSensitive = false, className, testId }) {
  return (
    <span
      className={className}
      data-testid={testId}
      dangerouslySetInnerHTML={highlightHtml(text, query, { caseSensitive })}
    />
  );
}
